import { PaginationRequestApiParams } from '../../../models';
import { authorizedFetch } from '../../helpers/authorizedFetch';
import { formatDate } from '../../helpers/date';
import trans from '../../helpers/trans';
import { generateApiUrl } from '../../helpers/url';
import isResourceCollectionDocument from '../../japi/guards/isResourceCollectionDocument';
import { JapiDocument } from '../../japi/types/Document';
import { DateFormat } from '../../types/dateFormatTypes';
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult';
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter';
import {
    Exchange,
    ExchangeFilters,
    ExchangeResource,
    ShiftExchange,
    ShiftExchangeResource,
} from './Exchange';
import {
    getExchangeFromApiData,
    getShiftExchangeFromApiData,
} from './ExchangeTransformers';

const exchangeIncludes = [
    'replacementUsers',
    'replacementUsers.person',
    'replacementUsers.experience',
    'owner',
    'owner.experience',
    'shift',
    'shift.shiftPlannings',
    'shift.shiftPlannings.user',
    'shift.shiftPlannings.user.person',
    'shift.shiftPlannings.user.experience',
    'shift.department',
    'shift.department.group',
];

const shiftExchangeInlcudes = [
    'shiftPlanning1',
    'shiftPlanning2',
    'shiftPlanning1.user',
    'shiftPlanning2.user',
    'shiftPlanning1.user.person',
    'shiftPlanning2.user.person',
    'shiftPlanning1.user.experience',
    'shiftPlanning2.user.experience',
    'shiftPlanning1.shift',
    'shiftPlanning2.shift',
    'shiftPlanning1.shift.shiftPlannings',
    'shiftPlanning2.shift.shiftPlannings',
    'shiftPlanning1.shift.department',
    'shiftPlanning2.shift.department',
    'shiftPlanning1.shift.department.group',
    'shiftPlanning2.shift.department.group',
];

export const getExchangesApiCall = async (
    startDate: Date,
    filters: ExchangeFilters,
    pagination?: PaginationRequestApiParams,
): Promise<FetchCollectionResult<Exchange[], string>> => {
    try {
        const { departments, employeeSearch, onlyWithReplies } = filters;

        const url = generateApiUrl({
            endpoint: '/exchanges',
            queryParams: {
                include: exchangeIncludes.join(','),
                ...(employeeSearch && { search: employeeSearch }),
                ...(departments && departments.length > 0 && { 'departments[]': departments }),
                hasReplacements: onlyWithReplies ? '1' : '0',
                startDate: formatDate(startDate, DateFormat.apiDateTime),
                ...(pagination && { 'page[number]': String(pagination.number || 1) }),
                ...(pagination && { 'page[size]': String(pagination.size) }),
            },
        });

        const response = await authorizedFetch(url);

        const doc: JapiDocument = await response.json();

        if (!isResourceCollectionDocument<ExchangeResource>(doc) || !doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        const exchanges = doc.data.map((exchangeResource) => getExchangeFromApiData(doc, exchangeResource));

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: exchanges,
            amountOfPages: doc.meta?.pagination.pages || 1,
        };
    } catch (error) {
        console.error('[getExchangesApiCall]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const getShiftExchangesApiCall = async (
    startDate: Date,
    filters: ExchangeFilters,
    pagination?: PaginationRequestApiParams,
): Promise<FetchCollectionResult<ShiftExchange[], string>> => {
    try {
        const { departments, employeeSearch } = filters;

        const url = generateApiUrl({
            endpoint: '/shift-exchanges',
            queryParams: {
                include: shiftExchangeInlcudes.join(','),
                ...(employeeSearch && { search: employeeSearch }),
                ...(departments && departments.length > 0 && { 'departments[]': departments }),
                ...(startDate && { startDate: formatDate(startDate, DateFormat.apiDateTime) }),
                statuses: JSON.stringify([1]),
                ...(pagination && { 'page[number]': String(pagination.number || 1) }),
                ...(pagination && { 'page[size]': String(pagination.size) }),
            },
        });

        const response = await authorizedFetch(url);

        const doc: JapiDocument = await response.json();

        if (!isResourceCollectionDocument<ShiftExchangeResource>(doc) || !doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        const shiftExchanges = doc.data.map(shiftExchangeResource => getShiftExchangeFromApiData(doc, shiftExchangeResource));

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: shiftExchanges,
            amountOfPages: doc.meta?.pagination.pages || 1,
        };
    } catch (error) {
        console.error('[getShiftExchangesApiCall]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const postExchangeLinkApiCall = async (
    exchangeId: string,
    userId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/exchanges/${exchangeId}/link/${userId}`,
        });

        const response = await authorizedFetch(url, {
            method: 'POST',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        });

        if (!response.ok) {
            const doc = await response.json();

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        };
    } catch (error) {
        console.error('[postExchangeLinkApiCall]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const deleteExchangeApiCall = async (
    exchangeId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/exchanges/${exchangeId}`,
        });

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        });

        if (!response.ok) {
            const doc = await response.json();

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        };
    } catch (error) {
        console.error('[deleteExchangeApiCall]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const patchApproveShiftExchangeApiCall = async (
    shiftExchangeId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/shift-exchanges/approve/${shiftExchangeId}`,
        });

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        });

        if (!response.ok) {
            const doc = await response.json();

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        };
    } catch (error) {
        console.error('[patchApproveShiftExchangeApi]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const patchDeclineShiftExchangeApiCall = async (
    shiftExchangeId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/shift-exchanges/decline/${shiftExchangeId}`,
        });

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        });

        if (!response.ok) {
            const doc = await response.json();

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            };
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        };
    } catch (error) {
        console.error('[patchDeclineShiftExchange]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};
